import { paths } from '@/config/paths';
import { affiliateId } from '@/consts/shop';
import {
  ValidateSignatureParams,
  ValidateSignatureResponse,
} from '@/types/api/registerQRApi';

export type RegisterQrQuery = Record<string, unknown>;

export type RegisterQrContext = {
  affiliateId: number;
  sig?: string;
  hasValidAffiliateId: boolean;
};

export type RegisterQrInitializationResult =
  | { status: 'ready' }
  | { status: 'invalid'; message: string }
  | { status: 'stale' };

type SignatureValidator = (
  params: ValidateSignatureParams,
) => Promise<ValidateSignatureResponse>;

const INVALID_QR_MESSAGE = '無効なQRコードです';
const QR_VALIDATION_FAILED_MESSAGE = 'QRコードの検証に失敗しました';

const getQueryValue = (value: unknown): string | undefined => {
  if (Array.isArray(value)) {
    return getQueryValue(value[0]);
  }
  if (typeof value === 'string') {
    return value || undefined;
  }
  if (typeof value === 'number') {
    return String(value);
  }
  return undefined;
};

const parseAffiliateId = (value: string | undefined): number | undefined => {
  if (!value) {
    return undefined;
  }
  const parsedValue = Number(value);
  return Number.isSafeInteger(parsedValue) && parsedValue > 0
    ? parsedValue
    : undefined;
};

export const getRegisterQrContext = (
  query: RegisterQrQuery,
): RegisterQrContext => {
  const affiliateIdQuery = getQueryValue(query.affiliate_id);
  const parsedAffiliateId = parseAffiliateId(affiliateIdQuery);

  return {
    affiliateId: parsedAffiliateId || affiliateId.DEFAULT,
    sig: getQueryValue(query.sig),
    hasValidAffiliateId: parsedAffiliateId !== undefined,
  };
};

export const parseStoreQrUrl = (
  qrData: string,
): Pick<RegisterQrContext, 'affiliateId' | 'sig'> | undefined => {
  let parsedUrl: URL;
  try {
    parsedUrl = new URL(qrData);
  } catch (error) {
    return undefined;
  }

  if (parsedUrl.pathname !== paths.registerQR) {
    return undefined;
  }

  const affiliateIdQuery = parsedUrl.searchParams.get('affiliate_id');
  const parsedAffiliateId = parseAffiliateId(affiliateIdQuery || undefined);
  const sig = parsedUrl.searchParams.get('sig') || undefined;
  if (!parsedAffiliateId || !sig) {
    return undefined;
  }

  return { affiliateId: parsedAffiliateId, sig };
};

export const buildRegisterQrPath = (
  currentAffiliateId: number | undefined,
  sig?: string,
): string => {
  const resolvedAffiliateId =
    currentAffiliateId !== undefined &&
    Number.isSafeInteger(currentAffiliateId) &&
    currentAffiliateId > 0
      ? currentAffiliateId
      : affiliateId.DEFAULT;
  const query = new URLSearchParams({
    affiliate_id: String(resolvedAffiliateId),
  });
  if (sig) {
    query.set('sig', sig);
  }
  return `${paths.registerQR}?${query.toString()}`;
};

export const initializeRegisterQr = async (
  context: RegisterQrContext,
  validateSignature: SignatureValidator,
  isCurrent: () => boolean,
): Promise<RegisterQrInitializationResult> => {
  if (!context.sig) {
    return isCurrent() ? { status: 'ready' } : { status: 'stale' };
  }

  if (!context.hasValidAffiliateId) {
    return isCurrent()
      ? { status: 'invalid', message: INVALID_QR_MESSAGE }
      : { status: 'stale' };
  }

  try {
    const response = await validateSignature({
      affiliate_id: context.affiliateId,
      sig: context.sig,
    });

    if (!isCurrent()) {
      return { status: 'stale' };
    }

    if (!response.data.success || !response.data.valid) {
      return {
        status: 'invalid',
        message: response.data.error?.message || INVALID_QR_MESSAGE,
      };
    }

    return { status: 'ready' };
  } catch (error) {
    return isCurrent()
      ? { status: 'invalid', message: QR_VALIDATION_FAILED_MESSAGE }
      : { status: 'stale' };
  }
};
